"use client";

import React, { useState, useEffect } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3].map((_, i) => <div key={`skeleton-${i}`} data-index={i} className="h-9 w-20 bg-muted animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.label === 'Transactions' ? '/Transactions' : item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center bg-transparent px-4 py-2 text-sm font-mono font-medium tracking-widest transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 text-foreground">
                <span className="relative py-1">
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary transition-all duration-300 group-hover:w-full"></span>
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border flex flex-col">
        <SheetTitle className="sr-only">Mobile Navigation</SheetTitle>
        <div className="flex flex-col gap-8 py-6 h-full">
          <Link to="/" onClick={() => setIsOpen(false)} className="inline-block">
            <img src="/img/blue_phoenix_ventures_logo.png" alt="Blue Phoenix Ventures" className="h-7 w-auto" />
          </Link>
          
          <nav className="flex flex-col gap-4">
            {loading ? <div className="flex flex-col gap-4">
                {[1, 2, 3].map((_, i) => <div key={`mobile-skeleton-${i}`} data-index={i} className="h-6 w-32 bg-muted animate-pulse" />)}
              </div> : menuItems.map((item, i) => <div key={item.id} data-index={i}>
                  <Link to={item.label === 'Transactions' ? '/Transactions' : item.href || "#"} className="text-lg font-default font-semibold text-foreground hover:text-primary transition-colors block py-2" onClick={() => setIsOpen(false)}>
                    {item.label}
                  </Link>
                </div>)}
          </nav>

          <div className="mt-auto flex flex-col gap-6 border-t border-border pt-6">
            <div className="flex items-center gap-3 text-foreground font-medium">
              <div className="flex items-center justify-center h-10 w-10 bg-primary/10 text-primary">
                <Phone className="h-5 w-5" />
              </div>
              (800) 555-0198
            </div>
            <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg font-mono tracking-wider">
              Secure Consultation
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="223686" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border shadow-md' : 'bg-background border-b border-transparent'}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <div className="flex-shrink-0 flex items-center">
          <Link to="/">
            <img src="https://bpvconsulting.com/wp-content/uploads/2026/06/Blue-Phoenix-Ventures-Logo-with-Text-Transparent.png" alt="Blue Phoenix Ventures" className="h-8 w-auto" />
          </Link>
        </div>

        <div className="flex-1 flex justify-center px-8">
          <MenuProvider menu_id="82">
            <DesktopMenu />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          <div className="hidden lg:flex items-center gap-2 mr-2 text-xs font-mono tracking-widest text-foreground uppercase">
            <Phone className="h-4 w-4 text-primary" />
            <span>(949) 698-8293</span>
          </div>
          
          <div className="relative hidden sm:inline-flex group">
            <div className="absolute -inset-[1px] bg-primary opacity-20 blur-[2px] group-hover:opacity-40 transition-opacity duration-500"></div>
            <Button variant="outline" className="relative border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground font-mono text-xs tracking-widest uppercase shadow-sm transition-all duration-300 overflow-hidden group">
              <Link to="/ContactPage" className="relative z-10">Secure Consultation</Link>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </Button>
          </div>

          <MenuProvider menu_id="82">
            <MobileMenu />
          </MenuProvider>
        </div>
      </div>
    </section>;
}